import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articleData } from '@/mocks/articleData';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleData[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleUrl = article ? `${SITE_URL}/article/${article.slug}` : SITE_URL;

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.description,
    datePublished: '2026-08-01',
    dateModified: '2026-08-10',
    author: {
      '@type': 'Organization',
      name: 'Basetao Sheet',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Sheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  } : null;

  const faqSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  const breadcrumbSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title.slice(0, 40),
        item: articleUrl,
      },
    ],
  } : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-20 pb-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center py-20">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                Article Not Found
              </h1>
              <p className="text-foreground-600 mb-6">
                The article you are looking for does not exist or has been moved.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main>
        <article>
          <header className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                    Home
                  </Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-800 font-medium">Article</span>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-600">{article.title.slice(0, 50)}...</span>
                </nav>

                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.h1}
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                  {article.description}
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-6 text-xs md:text-sm text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    <span>Updated August 2026</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    <span>12 min read</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    <span>Basetao Sheet Editorial Team</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                  prose-strong:text-foreground-900
                  prose-li:text-foreground-700 prose-li:leading-relaxed
                  prose-ul:my-5 prose-ul:pl-5
                  prose-li:marker:text-primary-500
                "
              >
                <div
                  className="mb-6 text-base md:text-lg text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.intro }}
                />

                {article.content.map((block, idx) => {
                  if (block.type === 'h2') {
                    return (
                      <h2 key={idx} id={`section-${idx}`}>
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'h3') {
                    return (
                      <h3 key={idx}>{block.text}</h3>
                    );
                  }
                  if (block.type === 'p') {
                    return (
                      <p key={idx}>{block.text}</p>
                    );
                  }
                  if (block.type === 'list') {
                    return (
                      <ul key={idx}>
                        {block.items?.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="mt-12 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-5">
                  {article.faq.map((item, idx) => (
                    <div key={idx}>
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-800 mb-2">
                        {item.question}
                      </h3>
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 p-6 md:p-8 rounded-xl bg-primary-500 text-white text-center">
                <h2 className="text-xl md:text-2xl font-heading font-semibold mb-3">
                  Ready to Start Shopping?
                </h2>
                <p className="text-sm md:text-base text-white/85 mb-6 max-w-lg mx-auto">
                  Access the latest Basetao spreadsheet with verified products, accurate pricing and real QC photos.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-accent-500 text-white font-semibold text-sm md:text-base hover:bg-accent-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>

              <nav className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                  Related Resources
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {article.relatedLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0">
                        <i className={link.href === '/' ? 'ri-home-4-line' : 'ri-file-text-line'}></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                        {link.label}
                      </span>
                    </Link>
                  ))}
                </div>
              </nav>
            </div>
          </div>
        </article>

        {articleSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
        )}

        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}

        {breadcrumbSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}